import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { updateUser as apiUpdateUser, ApiError } from '../services/apiService';
import { i18n } from '../i18n/i18n';
import { uploadAvatarApi, resolveAssetUrl } from '../services/apiService';
import { supabase, isMemberMode } from '../services/supabaseClient';
import { FEATURES } from '../constants/features';
import MySettings from './kid/MySettings';
import { AVATAR_PRESETS, avatarOrDefault, isUploadedAvatar } from '../utils/avatars';
import ConfirmDialog from './kid/ConfirmDialog';
import { KidButton } from './kid/KidUI';
import { Star } from './kid/KidIcon';
import { starsText } from './kid/KidTaskCard';
import { showToast } from './kid/Toast';
import { isNative, oauthRedirectTo, openSignInPage } from '../platform/native';

const AVATARS = AVATAR_PRESETS;

type Identity = NonNullable<Awaited<ReturnType<typeof supabase.auth.getUserIdentities>>['data']>['identities'][number];

const UserProfile: React.FC = () => {
  const { current_user, updateCurrentUser } = useAppContext();
  const [username, setUsername] = useState('');
  const [pin, setPin] = useState('');
  const [cpCurrent, setCpCurrent] = useState('');
  const [cpNew, setCpNew] = useState('');
  // Only accounts that sign in with email + password have a password to change (not Google/Facebook-only).
  const [hasPasswordLogin, setHasPasswordLogin] = useState(false);
  const [err, setErr] = useState('');
  const [identities, setIdentities] = useState<Identity[]>([]);
  const [unlinkTarget, setUnlinkTarget] = useState<Identity | null>(null);
  // The picture being uploaded, shown at once in the avatar spot (an object URL).
  const [preview, setPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  React.useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [newName, setNewName] = useState('');
  const [currentPin, setCurrentPin] = useState('');
  const [params] = useSearchParams();
  // PIN family members aren't real Supabase Auth users, so there's no OAuth identity to link.
  const canLinkAccounts = !isMemberMode();

  React.useEffect(() => {
    (async () => {
      try {
        if (!current_user || !canLinkAccounts) return;
        const { data } = await supabase.auth.getUserIdentities();
        setHasPasswordLogin((data?.identities || []).some((idn) => idn.provider === 'email'));
        // OAuth identities only — email/password isn't a "linked social account" and unlinking
        // it here (vs. the dedicated Change password flow) risks locking the user out.
        setIdentities((data?.identities || []).filter((idn) => idn.provider !== 'email' && idn.provider !== 'phone'));
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current_user?.id]);

  React.useEffect(() => {
    const linked = params.get('linked');
    if (linked === 'google') showToast({ message: i18n.t('user_profile.google_link_success') });
    else if (linked === 'facebook') showToast({ message: i18n.t('user_profile.facebook_link_success') });
  }, [params]);

  if (!current_user) {
    return <div>{i18n.t('user_profile.user_not_found')}</div>;
  }

  const handleEdit = () => {
    setNewName(current_user.name);
    setUsername((current_user as any).username || '');
    setPin('');
    setCurrentPin('');
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!newName.trim() && !username.trim() && !pin.trim()) {
      setIsEditing(false);
      return;
    }
    setErr('');
    // Client-side validation
    if (pin && (!/^\d{4,8}$/.test(pin))) {
      setErr(i18n.t('user_profile.pin_validation'));
      return;
    }
    if (username && username.trim().length < 2) {
      setErr(i18n.t('user_profile.username_validation'));
      return;
    }
    // Email is read-only (it comes from the login) and passwords change via the
    // "Change password" section; replacing an existing PIN needs the current PIN.
    try {
      await apiUpdateUser({
        name: newName.trim() || undefined,
        ...(username.trim() ? { username: username.trim() } : {}),
        ...(pin.trim() ? { pin: pin.trim(), current_pin: currentPin.trim() || undefined } : {}),
      });
    } catch (e) {
      const code = e instanceof ApiError ? e.code : undefined;
      if (code === 'current_pin_required') setErr(i18n.t('user_profile.current_pin_required'));
      else if (code === 'current_pin_incorrect') setErr(i18n.t('user_profile.current_pin_incorrect'));
      else setErr((e as Error)?.message || String(e));
      return;
    }
    // Reflect changes in local UI
    updateCurrentUser({ name: newName.trim() || current_user.name });
    setIsEditing(false);
    setNewName('');
    setUsername('');
    setPin('');
    setCurrentPin('');
    showToast({ message: i18n.t('user_profile.profile_updated') });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setNewName('');
    setPin('');
    setCurrentPin('');
  };

  const handleBack = () => {
    navigate('/home');
  };

  const isChild = (current_user as any).member_type === 'child' && !(current_user as any).is_admin;
  const avatarSrc = preview || resolveAssetUrl(avatarOrDefault((current_user as any).avatar_url));
  const inputClass = 'w-full min-h-[48px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark px-3 py-2 text-base focus:outline-none focus:border-kid-green';
  const labelClass = 'block text-base font-bold mb-1';
  const cardClass = 'rounded-kid-card border-2 border-kid-border dark:border-kid-border-dark bg-kid-card dark:bg-kid-card-dark p-4 md:p-5';
  const sectionTitle = 'font-kid-heading font-extrabold text-xl mb-3';

  const uploadAvatar = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const f = input.files?.[0];
    input.value = ''; // so picking the same file again still fires
    if (!f) return;
    // Any photo works (HEIC too): it is converted and shrunk before upload (utils/prepareImage).
    if (f.size > 25 * 1024 * 1024) { showToast({ message: i18n.t('user_profile.picture_too_big'), tone: 'error' }); return; }
    setPreview(URL.createObjectURL(f)); // show it right away, dimmed while it saves
    setUploading(true);
    try {
      const { avatar_url } = await uploadAvatarApi(f);
      updateCurrentUser({ avatar_url } as any);
      showToast({ message: i18n.t('user_profile.avatar_updated') });
    } catch (er: any) {
      setPreview(null); // back to the old picture
      showToast({ message: er?.message || i18n.t('user_profile.avatar_update_failed'), tone: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const pickAvatar = async (src: string) => {
    const before = (current_user as any).avatar_url;
    setPreview(null);
    updateCurrentUser({ avatar_url: src } as any); // show the choice at once
    try {
      await apiUpdateUser({ avatar_url: src } as any);
      showToast({ message: i18n.t('user_profile.avatar_updated') });
    } catch (e: any) {
      updateCurrentUser({ avatar_url: before } as any);
      showToast({ message: e?.message || i18n.t('user_profile.avatar_update_failed'), tone: 'error' });
    }
  };

  // "Remove picture": back to the basic ant; the server deletes the uploaded file.
  const removePicture = async () => {
    const before = (current_user as any).avatar_url;
    setPreview(null);
    updateCurrentUser({ avatar_url: null } as any);
    try {
      await apiUpdateUser({ avatar_url: null } as any);
      showToast({ message: i18n.t('pictures.removed') });
    } catch (e: any) {
      updateCurrentUser({ avatar_url: before } as any);
      showToast({ message: e?.message || i18n.t('user_profile.avatar_update_failed'), tone: 'error' });
    }
  };

  const linkProvider = async (provider: 'google' | 'facebook' | 'apple') => {
    const { data, error } = await supabase.auth.linkIdentity({
      provider,
      options: { redirectTo: oauthRedirectTo(`intent=link&provider=${provider}`), skipBrowserRedirect: isNative },
    });
    if (error) showToast({ message: error.message, tone: 'error' });
    else if (isNative && data?.url) await openSignInPage(data.url);
  };

  return (
    <div className="w-full max-w-2xl mx-auto px-4 md:px-8 py-4 md:py-7 space-y-6 text-kid-text dark:text-kid-text-dark">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handleBack}
          aria-label={i18n.t('user_profile.back_to_home')}
          className="w-12 h-12 rounded-full bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border dark:border-kid-border-dark text-2xl font-bold flex items-center justify-center shrink-0"
        >
          <span aria-hidden>←</span>
        </button>
        <h1 className="font-kid-heading font-extrabold text-3xl md:text-4xl">
          {isChild ? i18n.t('header.my_page') : i18n.t('user_profile.title')}
        </h1>
      </div>

      {err && <p role="alert" className="font-bold text-red-700 dark:text-red-300">{err}</p>}

      {/* you — the picture is also the upload button, so a new one shows right where it will be */}
      <section className={cardClass}>
        <input id="avatarUpload" type="file" accept="image/*" className="sr-only" onChange={uploadAvatar} disabled={uploading} />
        <div className="flex flex-wrap items-center gap-4">
          <label htmlFor="avatarUpload" className="relative shrink-0 cursor-pointer rounded-full" title={i18n.t('user_profile.upload')}>
            <img
              src={avatarSrc}
              alt={current_user.name}
              className={`w-24 h-24 md:w-28 md:h-28 rounded-full object-cover border-2 border-kid-border dark:border-kid-border-dark ${uploading ? 'opacity-50' : ''}`}
            />
            {uploading && (
              <span className="absolute inset-0 flex items-center justify-center font-kid-heading font-extrabold text-2xl" aria-live="polite">
                <span className="sr-only">{i18n.t('common.loading')}</span>
                <span aria-hidden className="w-8 h-8 rounded-full border-4 border-kid-green border-t-transparent animate-spin" />
              </span>
            )}
            <span aria-hidden className="absolute -bottom-1 -right-1 w-10 h-10 rounded-full bg-kid-green text-white flex items-center justify-center text-lg border-2 border-kid-card dark:border-kid-card-dark">
              📷
            </span>
            <span className="sr-only">{i18n.t('user_profile.upload')}</span>
          </label>
          {!isEditing && (
            <>
              <div className="flex-1 min-w-[10rem]">
                <p className="font-kid-heading font-extrabold text-2xl md:text-3xl leading-tight break-words">{current_user.name}</p>
                <p className="text-base">
                  {(current_user as any).is_admin ? i18n.t('user_profile.admin') : i18n.t('user_profile.family_member')}
                  {!isChild && (current_user as any).email ? ` · ${(current_user as any).email}` : ''}
                </p>
                <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-kid-peach dark:bg-amber-900/50 px-3 min-h-[36px] font-bold">
                  <Star size={18} /> {starsText(Number(current_user.points) || 0)}
                </span>
                {FEATURES.coins && <span className="ml-2 font-bold">🪙 {(current_user as any).coins ?? current_user.stars ?? 0}</span>}
              </div>
              <KidButton size="md" variant="secondary" onClick={handleEdit}>{i18n.t('user_profile.edit_profile')}</KidButton>
              <p className="w-full text-sm">{i18n.t('user_profile.tap_picture')}</p>
            </>
          )}
          {isEditing && <p className="flex-1 min-w-[10rem] text-base">{i18n.t('user_profile.tap_picture')}</p>}
        </div>

        {isEditing && <div className="mt-5">
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className={labelClass}>{i18n.t('user_profile.name')}</label>
              <input type="text" id="name" value={newName} onChange={(e) => setNewName(e.target.value)} className={inputClass} />
            </div>
            {!isChild && (
              <div>
                <label htmlFor="email" className={labelClass}>{i18n.t('user_profile.email')}</label>
                <input type="email" id="email" value={(current_user as any).email || ''} readOnly disabled aria-describedby="email-hint" className={`${inputClass} opacity-70 cursor-not-allowed`} />
                <p id="email-hint" className="mt-1 text-sm">{i18n.t('user_profile.email_managed_by_login')}</p>
              </div>
            )}
            <div>
              <label htmlFor="username" className={labelClass}>{i18n.t('user_profile.username_for_member_login')}</label>
              <input type="text" id="username" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" className={inputClass} />
            </div>
            <div>
              <label htmlFor="pin" className={labelClass}>{i18n.t('user_profile.pin_label')}</label>
              <input type="password" inputMode="numeric" id="pin" value={pin} onChange={(e) => setPin(e.target.value)} autoComplete="new-password" className={inputClass} />
            </div>
            {pin.trim() !== '' && (
              <div>
                <label htmlFor="current-pin" className={labelClass}>{i18n.t('user_profile.current_pin_label')}</label>
                <input type="password" inputMode="numeric" id="current-pin" autoComplete="current-password" value={currentPin} onChange={(e) => setCurrentPin(e.target.value)} className={inputClass} />
              </div>
            )}
            <div className="flex gap-3 pt-1">
              <KidButton size="md" variant="secondary" className="flex-1" onClick={handleCancel}>{i18n.t('user_profile.cancel')}</KidButton>
              <KidButton size="md" className="flex-[2]" onClick={handleSave}>{i18n.t('user_profile.save_changes')}</KidButton>
            </div>
          </div>
        </div>}

        {!preview && !uploading && isUploadedAvatar((current_user as any).avatar_url) && (
          <button
            type="button"
            onClick={removePicture}
            className="mt-4 inline-flex items-center gap-2 min-h-[44px] px-4 rounded-full border-2 border-kid-border-strong dark:border-kid-border-strong-dark font-bold text-base"
          >
            <span aria-hidden>🗑</span> {i18n.t('pictures.remove_picture')}
          </button>
        )}

        <h2 className={`${sectionTitle} mt-5`}>{i18n.t('user_profile.or_pick_one')}</h2>
        <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
          {AVATARS.map((src) => {
            const on = !preview && avatarOrDefault((current_user as any).avatar_url) === src;
            return (
              <button
                key={src}
                type="button"
                aria-pressed={on}
                aria-label={i18n.t('user_profile.choose_picture')}
                disabled={uploading}
                onClick={() => pickAvatar(src)}
                className={`aspect-square rounded-full p-1 border-4 ${on ? 'border-kid-green' : 'border-transparent'}`}
              >
                <img src={src} alt="" className="w-full h-full rounded-full object-cover" />
              </button>
            );
          })}
        </div>
      </section>

      {/* a child's own choices; grown-ups have the full Settings page */}
      {isChild && (
        <section>
          <h2 className="font-kid-heading font-extrabold text-2xl mb-3">{i18n.t('my_settings.title')}</h2>
          <MySettings />
        </section>
      )}

      {/* linked accounts — PIN family members have no linkable Supabase Auth identity */}
      {canLinkAccounts && (
        <section className={cardClass}>
          <h2 className={sectionTitle}>{i18n.t('user_profile.linked_accounts')}</h2>
          {identities.length === 0 ? (
            <p className="text-base mb-3">{i18n.t('user_profile.no_linked_accounts')}</p>
          ) : (
            <ul className="divide-y-2 divide-kid-border dark:divide-kid-border-dark mb-3">
              {identities.map((idn) => (
                <li key={idn.identity_id} className="flex items-center justify-between gap-3 min-h-[56px]">
                  <span className="font-bold text-lg capitalize">{idn.provider}</span>
                  <button type="button" onClick={() => setUnlinkTarget(idn)} className="min-h-[44px] px-4 rounded-full border-2 border-kid-border-strong dark:border-kid-border-strong-dark font-bold">
                    {i18n.t('user_profile.unlink')}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap gap-2">
            {!identities.some((idn) => idn.provider === 'google') && (
              <button type="button" onClick={() => linkProvider('google')} className="inline-flex items-center gap-2 min-h-[48px] px-4 rounded-full border-2 border-kid-border-strong dark:border-kid-border-strong-dark font-bold">
                <img src="/google_g.webp" alt="" aria-hidden="true" className="w-5 h-5" />
                {i18n.t('user_profile.link_to_google')}
              </button>
            )}
            {!identities.some((idn) => idn.provider === 'facebook') && (
              <button type="button" onClick={() => linkProvider('facebook')} className="inline-flex items-center gap-2 min-h-[48px] px-4 rounded-full border-2 border-kid-border-strong dark:border-kid-border-strong-dark font-bold">
                <img src="/Facebook_Logo.png" alt="" aria-hidden="true" className="w-5 h-5" />
                {i18n.t('user_profile.link_to_facebook')}
              </button>
            )}
            {!identities.some((idn) => idn.provider === 'apple') && (
              <button type="button" onClick={() => linkProvider('apple')} className="inline-flex items-center gap-2 min-h-[48px] px-4 rounded-full border-2 border-kid-border-strong dark:border-kid-border-strong-dark font-bold">
                <img src="/Apple_Logo.png" alt="" aria-hidden="true" className="w-5 h-5 dark:invert" />
                {i18n.t('user_profile.link_to_apple')}
              </button>
            )}
          </div>
        </section>
      )}

      {/* password — only email/password logins have one (PIN members change their PIN above) */}
      {canLinkAccounts && hasPasswordLogin && (
        <section className={cardClass}>
          <h2 className={sectionTitle}>{i18n.t('user_profile.change_password')}</h2>
          <div className="space-y-4">
            <div>
              <label htmlFor="cp-current" className={labelClass}>{i18n.t('user_profile.current_password')}</label>
              <input id="cp-current" type="password" autoComplete="current-password" value={cpCurrent} onChange={(e) => setCpCurrent(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label htmlFor="cp-new" className={labelClass}>{i18n.t('user_profile.new_password')}</label>
              <input id="cp-new" type="password" autoComplete="new-password" value={cpNew} onChange={(e) => setCpNew(e.target.value)} className={inputClass} />
            </div>
            <KidButton
              size="md"
              disabled={!cpCurrent || !cpNew}
              onClick={async () => {
                try {
                  const { changePassword } = await import('../services/apiService');
                  await changePassword(cpCurrent, cpNew);
                  showToast({ message: i18n.t('user_profile.password_changed') });
                  setCpCurrent('');
                  setCpNew('');
                } catch (e: any) {
                  showToast({ message: e?.message || i18n.t('user_profile.password_change_failed'), tone: 'error' });
                }
              }}
            >
              {i18n.t('user_profile.save_new_password')}
            </KidButton>
          </div>
        </section>
      )}

      <ConfirmDialog
        open={!!unlinkTarget}
        title={unlinkTarget ? i18n.t('user_profile.unlink_confirm').replace('{{provider}}', unlinkTarget.provider) : ''}
        confirmLabel={i18n.t('user_profile.unlink')}
        cancelLabel={i18n.t('user_profile.cancel')}
        onCancel={() => setUnlinkTarget(null)}
        onConfirm={async () => {
          if (!unlinkTarget) return;
          const { error } = await supabase.auth.unlinkIdentity(unlinkTarget);
          if (error) throw new Error(error.message || i18n.t('user_profile.unlink_failed'));
          setIdentities(identities.filter((x) => x.identity_id !== unlinkTarget.identity_id));
          setUnlinkTarget(null);
          showToast({ message: i18n.t('user_profile.account_unlinked') });
        }}
      />
    </div>
  );
};

export default UserProfile;
